import {
  Box,
  Flex,
  Select,
  Loader,
  Combobox,
  Button,
  Divider,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { omitBy, isNil } from "lodash";
import { If, Then } from "react-if";

import { useWindowScroll } from "@mantine/hooks";
import { useSearchParams } from "react-router";
import { actions, store, useTrackedStore } from "../../../../store/useStore";
import {
  useApplicationCampus,
  useApplicationCampusPrograms,
  useGetAllAmbassadors,
  useGetAllInstitutions,
  useGetCitiesByState,
  useGetCountriesByRegion,
  useGetDiscipline,
  useGetStatesByCountry,
  useGetStudyLevel,
} from "../../../../api/ambassador/useAmbassador";
import { AppliedFilters } from "../../components/ui/AppliedFilters.component";

export default function AmbassadorFilters({ }) {
  const [searchParams] = useSearchParams();

  const {
    filter: { data: filterData },
    ambassadors: { page, pageSize },
  } = useTrackedStore();

  const filters = filterData();
  const [scroll] = useWindowScroll();
  const _page = page();
  const _pageSize = pageSize();

  const filterForm = useForm({
    initialValues: { ...filters },
    onValuesChange: (values) => {
      actions.filter.data({ ...store.filter.data(), ...values });
      actions.ambassadors.resetPage();
    },
  });

  const { data: countries } = useGetCountriesByRegion({
    number_only: true,
  });
  const { data: disciplines } = useGetDiscipline({
    text_only: true,
  });
  const { data: states } = useGetStatesByCountry({
    country_id: filterForm.values.country_id,
    enabled: !!filterForm.values.country_id,
    params: {
      number_only: true,
    },
  });
  const { data: cities } = useGetCitiesByState({
    params: {
      country_id: filterForm.values.country_id,
      state_id: filterForm.values.state_id,
      number_only: true,
    },
    enabled: !!filterForm.values.state_id && !!filterForm.values.country_id,
  });
  const { data: universities } = useGetAllInstitutions({
    number_only: true,
  });
  const { data: campuses } = useApplicationCampus({
    enabled: !!filterForm.values.institution_id,
    institution_id: filterForm.values.institution_id,
  });

  const { data: programs } = useApplicationCampusPrograms({
    enabled:
      !!filterForm.values.campus_id && !!filterForm.values.institution_id,
    campus_id: filterForm.values.campus_id,
    params: {
      institution_id: filterForm.values.institution_id,
    },
  });

  const { data: studyLevels } = useGetStudyLevel();

  const {
    data: filtersData,
    isLoading: isAmbassadorFilterLoading,
    isFetching: isAmbassadorFilterFetching,
  } = useGetAllAmbassadors({
    per_page: _pageSize,
    page: _page,
    params: {
      ...filters,
      country_id: filters?.country_id,
      state_id: filters?.state_id,
      city_id: filters?.city_id,
    },
  });

  const has_country_id = searchParams.get("has_country_id");
  const has_state_id = searchParams.get("has_state_id");
  const has_city_id = searchParams.get("has_city_id");
  const has_university_id = searchParams.get("has_university_id");
  const has_program_id = searchParams.get("has_program_id");
  const has_campus_id = searchParams.get("has_campus_id");
  const has_study_level_id = searchParams.get("has_study_level_id");
  const has_discipline_id = searchParams.get("has_discipline_id");

  return (
    <>
      <Box py={10}>
        <Flex wrap="wrap" justify="space-between" gap={10}>
          <Flex wrap="wrap" gap={10}>
            {has_country_id && (
              <Select
                w={110}
                clearable
                searchable
                placeholder="Country"
                defaultValue={filterForm.values.country_id}
                id="ambassador-filter-form-field-country_id"
                rightSection={
                  isAmbassadorFilterFetching || isAmbassadorFilterLoading ? (
                    <Loader size={14} />
                  ) : (
                    <Combobox.Chevron size="sm" />
                  )
                }
                data={
                  countries?.map((country: any) => ({
                    label: country?.label,
                    value: String(country?.value),
                  })) ?? []
                }
                {...filterForm.getInputProps("country_id")}
                onChange={(value) => {
                  filterForm.setFieldValue("country_id", value);
                  filterForm.setFieldValue("state_id", null);
                  filterForm.setFieldValue("city_id", null);
                }}
              />
            )}

            {has_state_id && (
              <Select
                w={150}
                clearable
                searchable
                placeholder="State/Province"
                id="state-filter-form-field"
                defaultValue={filterForm.values.country_id}
                rightSection={
                  isAmbassadorFilterFetching || isAmbassadorFilterLoading ? (
                    <Loader size={14} />
                  ) : (
                    <Combobox.Chevron size="sm" />
                  )
                }
                data={
                  states?.data?.length > 0
                    ? states?.data?.map((state: any) => ({
                      label: state?.label,
                      value: String(state?.value),
                    }))
                    : []
                }
                {...filterForm.getInputProps("state_id")}
                onChange={(value) => {
                  filterForm.setFieldValue("state_id", value);
                  filterForm.setFieldValue("city_id", null);
                }}
              />
            )}

            {has_city_id && (
              <Select
                w={80}
                clearable
                searchable
                placeholder="City"
                id="ambassador-filter-form-field-english-level"
                rightSection={
                  isAmbassadorFilterFetching || isAmbassadorFilterLoading ? (
                    <Loader size={14} />
                  ) : (
                    <Combobox.Chevron size="sm" />
                  )
                }
                data={
                  cities?.length > 0
                    ? cities?.map((city: any) => ({
                      label: city?.label,
                      value: String(city?.value),
                    }))
                    : []
                }
                {...filterForm.getInputProps("city_id")}
              />
            )}

            {has_university_id && (
              <Select
                w={120}
                clearable
                searchable
                placeholder="University"
                defaultValue={(filterForm.values as any)?.age}
                id="ambassador-filter-form-field-age"
                rightSection={
                  isAmbassadorFilterFetching || isAmbassadorFilterLoading ? (
                    <Loader size={14} />
                  ) : (
                    <Combobox.Chevron size="sm" />
                  )
                }
                data={universities ?? []}
                {...filterForm.getInputProps("institution_id")}
                onChange={(value: any) => {
                  filterForm.setFieldValue("institution_id", value);
                  filterForm.setFieldValue("campus_id", null);
                  filterForm.setFieldValue("program_id", null);
                }}
              />
            )}

            {has_campus_id && (
              <Select
                w={120}
                clearable
                searchable
                placeholder="Campus"
                id="ambassador-filter-form-field-campus"
                rightSection={
                  isAmbassadorFilterFetching || isAmbassadorFilterLoading ? (
                    <Loader size={14} />
                  ) : (
                    <Combobox.Chevron size="sm" />
                  )
                }
                data={campuses ?? []}
                {...filterForm.getInputProps("campus_id")}
                onChange={(value: any) => {
                  filterForm.setFieldValue("campus_id", value);
                  filterForm.setFieldValue("program_id", null);
                }}
              />
            )}

            {has_program_id && (
              <Select
                w={130}
                clearable
                searchable
                placeholder="Programs"
                id="ambassador-filter-form-field-programs"
                rightSection={
                  isAmbassadorFilterFetching || isAmbassadorFilterLoading ? (
                    <Loader size={14} />
                  ) : (
                    <Combobox.Chevron size="sm" />
                  )
                }
                data={programs ?? []}
                {...filterForm.getInputProps("program_id")}
              />
            )}

            {has_discipline_id && (
              <Select
                w={130}
                clearable
                searchable
                placeholder="Discipline"
                id="ambassador-filter-form-field-discipline"
                rightSection={
                  isAmbassadorFilterFetching || isAmbassadorFilterLoading ? (
                    <Loader size={14} />
                  ) : (
                    <Combobox.Chevron size="sm" />
                  )
                }
                data={
                  disciplines?.map((discipline: any) => ({
                    label: discipline?.label,
                    value: String(discipline?.value),
                  })) ?? []
                }
                {...filterForm.getInputProps("discipline_id")}
              />
            )}

            {has_study_level_id && (
              <Select
                w={140}
                clearable
                searchable
                placeholder="Study Level"
                id="ambassador-filter-form-field-study-level"
                rightSection={
                  isAmbassadorFilterFetching || isAmbassadorFilterLoading ? (
                    <Loader size={14} />
                  ) : (
                    <Combobox.Chevron size="sm" />
                  )
                }
                data={
                  studyLevels?.map((country: any) => ({
                    label: country?.label,
                    value: String(country?.value),
                  })) ?? []
                }
                {...filterForm.getInputProps("study_level_id")}
              />
            )}
          </Flex>

          <If condition={store.filter.applied()}>
            <Then>
              <Divider orientation="vertical" />

              <Button
                onClick={() => {
                  actions.filter.reset();
                  actions.ambassadors.resetPage();

                  filterForm?.setValues({
                    country_id: null,
                    state_id: null,
                    city_id: null,
                    study_level_id: null,
                    discipline_id: null,
                    institution_id: null,
                    campus_id: null,
                    program_id: null,
                  });
                }}
                size="sm"
                radius="sm"
                id="ambassador-filter-form-reset-btn"
              >
                Reset All
              </Button>
            </Then>
          </If>
        </Flex>
      </Box>

      <If condition={store.filter.applied()}>
        <Then>
          <Box
            style={{
              zIndex: 3,
              borderBottom: scroll.y > 270 ? "1px solid #DADCE0" : "none",
            }}
            bg={scroll.y > 270 ? "white" : "transparent"}
            pt={scroll.y > 270 ? 0 : 10}
            pb={20}
            top={90}
            pos="sticky"
          >
            <AppliedFilters
              form={filterForm}
              data={{
                countries: countries,
                states: states?.data,
                cities: cities,
                universities: universities,
                campuses: campuses,
                programs: programs,
                discpline: disciplines,
                studyLevels: studyLevels,
              }}
              filterKeys={{
                country_id: "countries",
                state_id: "states",
                city_id: "cities",
                institution_id: "universities",
                campus_id: "campuses",
                program_id: "programs",
                discipline_id: "disciplines",
                study_level_id: "studyLevels",
              }}
              filterTypes={{
                country_id: "number",
                state_id: "number",
                city_id: "number",
                institution_id: "number",
                campus_id: "number",
                program_id: "number",
                discipline_id: "number",
                study_level_id: "number",
              }}
              appliedFilters={
                omitBy(filters, (value: any) => {
                  return isNil(value) || value?.length === 0;
                }) ?? {}
              }
              dependentFilter={{
                country_id: ["state_id", "city_id"],
                state_id: ["city_id"],
                institution_id: ["campus_id", "program_id"],
                campus_id: ["program_id"],
              }}
            />
          </Box>
        </Then>
      </If>
    </>
  );
}
